import { useEffect, useRef } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getFaceMatchingRepository } from '../infrastructure/repositories/container';
import { LocalFaceEmbeddingRepository } from '../infrastructure/repositories/LocalFaceEmbeddingRepository';
import {
  FACE_EMBEDDINGS_EMPTY_POLL_MS,
  FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS,
} from '@/shared/config/terminal.config';

const localEmbedRepo = new LocalFaceEmbeddingRepository();

export const faceKeys = {
  all:            ['face'] as const,
  ready:          () => [...faceKeys.all, 'ready'] as const,
  embeddingCount: (idAccessRole: number) => [...faceKeys.all, 'embeddings', idAccessRole] as const,
} as const;

/**
 * Initializes the ONNX models and reports their status.
 * Runs once on mount — models are cached after first load.
 */
export function useFaceMatcherReadyQuery() {
  return useQuery({
    queryKey: faceKeys.ready(),
    queryFn:  async () => {
      const repo = getFaceMatchingRepository();
      await repo.initialize();
      return repo.isReady();
    },
    // CRITICAL: Execute queryFn even when offline — ONNX model is bundled, no network required
    networkMode: 'always',
    staleTime: Infinity,
    retry:     1,
    retryDelay: 3000,
  });
}

export interface UseFaceEmbeddingsCountResult {
  /** Number of 512-d embeddings cached locally for the active access role. */
  data: number | undefined;
  isLoading: boolean;
  /**
   * `true` iff the empty-attempt cap has been reached AND the latest count is 0.
   * When `true`, polling has stopped and ScanningPage MUST fall back to non-facial
   * auth methods (PIN / Card) via the EmbeddingsUnavailableTooltip UX.
   */
  noFacialEnrollments: boolean;
  /** Current empty-attempt counter for the active access role. Exposed for tests / DEV. */
  attempt: number;
  /** Underlying TanStack `fetchStatus`. Kept for parity with legacy call sites. */
  fetchStatus: 'fetching' | 'paused' | 'idle';
  /** Underlying TanStack `error`. */
  error: unknown;
  /** Manual refetch trigger (bypasses `refetchInterval` bounds). */
  refetch: () => void;
}

/**
 * Number of 512-d embeddings cached locally for the given access role.
 *
 * Empty-set polling is bounded: while `count === 0`, the query re-runs every
 * `FACE_EMBEDDINGS_EMPTY_POLL_MS` up to `FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS` attempts.
 * On the final empty attempt, polling stops and `noFacialEnrollments` becomes `true`.
 * Switching `idAccessRole` resets the counter — the new role starts a fresh window.
 */
export function useFaceEmbeddingsCountQuery(
  idAccessRole: number | null,
  options?: Partial<Pick<UseQueryOptions<number>, 'refetchInterval'>>,
): UseFaceEmbeddingsCountResult {
  // Attempt counter is external to TanStack cache because `refetchInterval`
  // callbacks only receive `query.state.data`, not persisted metadata. Keyed
  // by `idAccessRole` so we can reset explicitly on role change.
  const attemptsRef = useRef<Map<number, number>>(new Map());

  // Reset counter when idAccessRole changes so the new role restarts polling.
  useEffect(() => {
    if (idAccessRole !== null) {
      attemptsRef.current.delete(idAccessRole);
    }
  }, [idAccessRole]);

  const query = useQuery<number>({
    queryKey: faceKeys.embeddingCount(idAccessRole ?? 0),
    queryFn:  async () => {
      const rows  = await localEmbedRepo.getByAccessRole(idAccessRole!);
      const count = rows.length;

      if (idAccessRole !== null) {
        const prev = attemptsRef.current.get(idAccessRole) ?? 0;
        const next = count === 0 ? prev + 1 : 0;
        attemptsRef.current.set(idAccessRole, next);

        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug('[FaceEmbeddingsCountQuery]', {
            idAccessRole,
            count,
            attempt: next,
            ts:      Date.now(),
          });
        }
      }

      return count;
    },
    enabled:  idAccessRole !== null,
    // CRITICAL: Execute queryFn even when offline — pure Dexie read, no network required
    networkMode: 'always',
    staleTime: 30_000,
    refetchInterval: (q) => {
      const count = q.state.data;
      if (count === undefined || count > 0) return false;
      const attempt = idAccessRole !== null
        ? (attemptsRef.current.get(idAccessRole) ?? 0)
        : 0;
      return attempt < FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS
        ? FACE_EMBEDDINGS_EMPTY_POLL_MS
        : false;
    },
    ...options,
  });

  const attempt = idAccessRole !== null
    ? (attemptsRef.current.get(idAccessRole) ?? 0)
    : 0;

  const noFacialEnrollments =
    idAccessRole !== null
    && query.data === 0
    && attempt >= FACE_EMBEDDINGS_MAX_EMPTY_ATTEMPTS;

  return {
    data:                query.data,
    isLoading:           query.isLoading,
    noFacialEnrollments,
    attempt,
    fetchStatus:         query.fetchStatus,
    error:               query.error,
    refetch:             () => { void query.refetch(); },
  };
}
